import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  GraduationCap,
  FileText,
  Calendar,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  CheckCircle,
  Clock,
  ChevronDown,
  Shield,
  Building2,
  Briefcase,
  Phone,
  UserPlus,
  MessageCircle,
  Image,
  ClipboardList,
  BookOpen,
  Activity,
  MapPin,
  BookOpenCheck,
  Scale,
  Globe,
  Layers,
  ArrowLeft,
  Home,
  RefreshCw,
  GraduationCap as IBIcon
} from 'lucide-react';

const API_URL = window.location.origin;

const roleColors = {
  super_admin: 'bg-red-500',
  content_approver: 'bg-amber-500',
  content_editor: 'bg-blue-500',
  viewer: 'bg-gray-500'
};

const roleLabels = {
  super_admin: 'Super Admin',
  content_approver: 'Approver',
  content_editor: 'Editor',
  viewer: 'Viewer'
};

export const AdminLayout = ({ children }) => {
  const { user, token, logout, hasPermission, hasModuleAccess, canApprove, isAdmin } = useCMSAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Force light mode for admin panel - ensures input text is visible
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('light');
    root.classList.remove('dark');
  }, []);

  useEffect(() => {
    // Fetch pending drafts count
    const fetchPendingCount = async () => {
      if (canApprove() && token) {
        try {
          const res = await fetch(`${API_URL}/api/cms/drafts?status=pending`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setPendingCount(data.length);
          }
        } catch (e) {
          console.error('Failed to fetch pending count');
        }
      }
    };
    fetchPendingCount();
  }, [token, canApprove]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  // Sidebar definition: each item's `module` MUST match a module id in
  // CMS_MODULES (see /app/backend/routes/admin.py). The Role Permission
  // matrix in /admin/users is the single source of truth for visibility.
  // Do NOT add `showFor` hardcodes here — toggle access in the matrix instead.
  const navItems = [
    // === OVERVIEW ===
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      href: '/admin/dashboard',
      module: 'dashboard'
    },
    {
      icon: Clock,
      label: 'Pending Approvals',
      href: '/admin/pending',
      module: 'pending_approvals',
      badge: pendingCount > 0 ? pendingCount : null
    },
    // === WEBSITE CONTENT ===
    { type: 'divider', label: 'Website Content' },
    { icon: Layers,    label: 'Page Content',      href: '/admin/page-content',   module: 'page_content' },
    { icon: Settings,  label: 'Homepage & Footer', href: '/admin/site-settings',  module: 'homepage_footer' },
    { icon: FileText,  label: 'Blog Posts',        href: '/admin/blog',           module: 'blog_posts' },
    { icon: Calendar,  label: 'Events',            href: '/admin/events',         module: 'events' },
    { icon: Briefcase, label: 'Services',          href: '/admin/services',       module: 'services' },
    { icon: Phone,     label: 'Contact Page',      href: '/admin/contact',        module: 'contact_page' },
    { icon: MapPin,    label: 'Locations',         href: '/admin/locations',      module: 'locations' },
    { icon: FileText,  label: 'Legal Pages',       href: '/admin/legal-pages',    module: 'legal_pages' },
    { icon: Users,     label: 'Team Manager',      href: '/admin/team',           module: 'team_manager' },
    { icon: BookOpen,  label: 'Resource Library',  href: '/admin/resources',      module: 'resource_library' },
    { icon: Image,     label: 'Assets Manager',    href: '/admin/assets',         module: 'assets_manager' },
    { icon: Image,     label: 'Destination Banners', href: '/admin/destination-banners', module: 'assets_manager' },
    { icon: Image,     label: 'Homepage Hero Copy',  href: '/admin/homepage-copy',       module: 'assets_manager' },
    { icon: Image,     label: 'CMS Sections',      href: '/admin/sections',       module: 'assets_manager' },
    // === UNIVERSITIES ===
    { type: 'divider', label: 'Universities' },
    { icon: GraduationCap, label: 'All Universities',     href: '/admin/universities',          module: 'all_universities' },
    { icon: FileText,      label: 'University Pages',     href: '/admin/university-pages',      module: 'university_pages' },
    { icon: Building2,     label: 'Partner Universities', href: '/admin/partners',              module: 'partner_universities' },
    { icon: CheckCircle,   label: 'University Approvals', href: '/admin/university-approvals',  module: 'university_approvals' },
    // === SEO PAGES ===
    { type: 'divider', label: 'SEO Pages' },
    { icon: BookOpenCheck, label: 'Programs',          href: '/admin/programs',             module: 'programs' },
    { icon: Scale,         label: 'Comparisons',       href: '/admin/comparisons',          module: 'comparisons' },
    { icon: Globe,         label: 'Dest. Compare',     href: '/admin/destination-compare',  module: 'dest_compare' },
    { icon: IBIcon,        label: 'IB Pathway',        href: '/admin/ib-pathway',           module: 'ib_pathway' },
    // === STUDENTS & LEADS ===
    { type: 'divider', label: 'Students & Leads' },
    { icon: UserPlus,      label: 'Lead Management',     href: '/admin/leads',                module: 'lead_management' },
    { icon: GraduationCap, label: 'Student Management',  href: '/admin/students',             module: 'student_management' },
    { icon: RefreshCw,     label: 'Zoho CRM Sync',       href: '/admin/zoho-sync',            module: 'zoho_crm_sync' },
    { icon: Activity,      label: 'Student Activities',  href: '/admin/student-activities',   module: 'student_activities' },
    { icon: ClipboardList, label: 'Applications',        href: '/admin/applications',         module: 'applications' },
    { icon: MessageCircle, label: 'Chat & Counselors',   href: '/admin/chat',                 module: 'chat_counselors' },
    { icon: Shield,        label: 'Score Verification',  href: '/admin/english-verification', module: 'score_verification' },
    // === SETTINGS ===
    { type: 'divider', label: 'Settings' },
    { icon: BookOpen, label: 'Question Bank',     href: '/admin/question-bank', module: 'question_bank' },
    { icon: FileText, label: 'Documentation',    href: '/admin/documentation', module: 'documentation' },
    { icon: Users,    label: 'User Management',  href: '/admin/users',         module: 'user_management' },
  ];

  // Filter nav items based on module permissions. Single source of truth =
  // the role's permission list from the backend. Super Admin sees everything.
  const filteredNavItems = navItems.filter((item, index, array) => {
    // Super admin bypass - always sees everything
    if (user?.role === 'super_admin') {
      return true;
    }

    // For dividers: show only if at least one item in this section is visible
    if (item.type === 'divider') {
      for (let i = index + 1; i < array.length; i++) {
        if (array[i].type === 'divider') break;
        if (hasModuleAccess(array[i].module)) return true;
      }
      return false;
    }

    // For regular items: matrix-driven access only
    return hasModuleAccess(item.module);
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-ux-charcoal transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-ux-cyan to-ux-violet flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-white font-bold">UniXperts CMS</span>
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white/60 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item, index) => {
              // Handle section dividers
              if (item.type === 'divider') {
                return (
                  <div key={`divider-${index}`} className="pt-4 pb-2 px-3">
                    <span className="text-xs font-semibold text-white/40 uppercase tracking-wider">
                      {item.label}
                    </span>
                  </div>
                );
              }
              
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-ux-cyan/20 text-ux-cyan' 
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="flex-1">{item.label}</span>
                  {item.badge && (
                    <Badge className="bg-ux-coral text-white text-xs px-1.5">
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ux-cyan to-ux-violet flex items-center justify-center text-white font-bold">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <div className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${roleColors[user?.role] || 'bg-gray-500'}`} />
                  <span className="text-white/60 text-xs">{roleLabels[user?.role] || 'User'}</span>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-0">
        {/* Top Header */}
        <header className="sticky top-0 z-40 h-16 bg-white dark:bg-slate-800 border-b dark:border-slate-700 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-ux-charcoal dark:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Back to Dashboard button - shows on all pages except dashboard */}
            {location.pathname !== '/admin/dashboard' && (
              <Link
                to="/admin/dashboard"
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>
            )}
          </div>

          <div className="flex items-center gap-4 ml-auto">
            {/* Pending Approvals Indicator */}
            {canApprove() && pendingCount > 0 && (
              <Link 
                to="/admin/pending"
                className="relative p-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-ux-coral text-white text-xs rounded-full flex items-center justify-center">
                  {pendingCount}
                </span>
              </Link>
            )}

            {/* Role Badge */}
            <Badge className={`${roleColors[user?.role]} text-white`}>
              {roleLabels[user?.role]}
            </Badge>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
